/**
 * `.gproj`（ZIP / fflate）の読み書き。染色体級を扱うため、配列本体は
 * 固定長チャンクごとの ZIP エントリに分ける（v3・§0.1）。読みは v1/v2 とも互換。
 *
 * 書きも読みもストリーム。全体をメモリに載せない。
 */

import { Unzip, UnzipInflate, Zip, ZipDeflate } from 'fflate';
import {
  DEFAULT_CHUNK_SIZE,
  MAX_GPROJ_ENTRIES,
  MAX_GPROJ_EXPANDED_BYTES,
  MAX_GPROJ_ZIP_BYTES,
  MAX_MANIFEST_BYTES,
  MAX_SEQUENCES_PER_PROJECT,
  MAX_SEQUENCE_LENGTH,
} from '../constants';
import { GenomicFeatureV1 } from '../annotation/types';
import { Topology } from '../sequence/interval';
import { CHECKSUM_ALGORITHM, ChunkMerkleHasher } from './checksum';
import {
  GPROJ_FORMAT_VERSION_V3,
  GprojFormatError,
  GprojManifest,
  GprojSequenceEntry,
  MANIFEST_PATH,
  V1_FEATURES_PATH,
  V1_SEQUENCE_PATH,
  assertSafeEntryPath,
  chunkEntryPath,
  featuresEntryPath,
} from './gprojTypes';

const CHECKSUMS_PATH = 'checksums.json';
const DEFAULT_COMPRESSION_LEVEL = 6;

// ---------------------------------------------------------------- 書き出し

export interface GprojWriteSequence {
  id: string;
  name: string;
  description?: string;
  length: number;
  topology: Topology;
  chunkSize: number;
  chunkCount: number;
  features: GenomicFeatureV1[];
  readChunk(index: number): Promise<Uint8Array>;
}

export interface GprojWriteParams {
  project: { id: string; name: string; createdAt?: string; updatedAt?: string };
  sequences: GprojWriteSequence[];
  uiPreferences?: Record<string, unknown>;
  onData: (data: Uint8Array) => Promise<void> | void;
  onProgress?: (progress: { bytesWritten: number; chunksWritten: number; totalChunks: number }) => void;
  compressionLevel?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  /** チェックサム（チャンク Merkle 根）を作る。既定 true */
  withChecksums?: boolean;
}

export async function writeGprojStream(params: GprojWriteParams): Promise<GprojManifest> {
  if (params.sequences.length > MAX_SEQUENCES_PER_PROJECT) {
    throw new GprojFormatError(
      `配列が多すぎます: ${params.sequences.length}（上限 ${MAX_SEQUENCES_PER_PROJECT}）`
    );
  }

  const now = new Date().toISOString();
  const manifest: GprojManifest = {
    formatVersion: GPROJ_FORMAT_VERSION_V3,
    generator: '@kensuke/genome-core',
    project: {
      id: params.project.id,
      name: params.project.name,
      createdAt: params.project.createdAt ?? now,
      updatedAt: params.project.updatedAt ?? now,
    },
    sequences: params.sequences.map<GprojSequenceEntry>((sequence) => ({
      id: sequence.id,
      name: sequence.name,
      description: sequence.description,
      length: sequence.length,
      topology: sequence.topology,
      chunkSize: sequence.chunkSize,
      chunkCount: sequence.chunkCount,
      checksumAlgorithm: params.withChecksums === false ? undefined : CHECKSUM_ALGORITHM,
    })),
    uiPreferences: params.uiPreferences,
  };

  const pendingOutput: Uint8Array[] = [];
  let zipError: Error | null = null;
  let bytesWritten = 0;

  const zip = new Zip((error, data, _final) => {
    if (error) {
      zipError = error;
      return;
    }
    if (data && data.length > 0) pendingOutput.push(data);
  });

  const drain = async () => {
    while (pendingOutput.length > 0) {
      const data = pendingOutput.shift() as Uint8Array;
      bytesWritten += data.length;
      if (bytesWritten > MAX_GPROJ_ZIP_BYTES) {
        throw new GprojFormatError(
          `.gproj が上限を超えました: ${bytesWritten} バイト（上限 ${MAX_GPROJ_ZIP_BYTES}）`
        );
      }
      await params.onData(data);
    }
    if (zipError) throw zipError;
  };

  const level = params.compressionLevel ?? DEFAULT_COMPRESSION_LEVEL;
  const addEntry = async (path: string, data: Uint8Array) => {
    assertSafeEntryPath(path);
    const entry = new ZipDeflate(path, { level });
    zip.add(entry);
    entry.push(data, true);
    await drain();
  };

  const encoder = new TextEncoder();
  const totalChunks = params.sequences.reduce((sum, sequence) => sum + sequence.chunkCount, 0);
  let chunksWritten = 0;

  await addEntry(MANIFEST_PATH, encoder.encode(JSON.stringify(manifest, null, 2)));

  for (const sequence of params.sequences) {
    await addEntry(
      featuresEntryPath(sequence.id),
      encoder.encode(JSON.stringify({ sequenceId: sequence.id, features: sequence.features }))
    );
  }

  const checksums: Record<string, string> = {};
  for (const sequence of params.sequences) {
    const hasher = params.withChecksums === false ? null : new ChunkMerkleHasher();
    for (let index = 0; index < sequence.chunkCount; index += 1) {
      const codes = await sequence.readChunk(index);
      await hasher?.push(codes);
      await addEntry(chunkEntryPath(sequence.id, index), codes);
      chunksWritten += 1;
      params.onProgress?.({ bytesWritten, chunksWritten, totalChunks });
    }
    if (hasher) checksums[sequence.id] = await hasher.root();
  }

  if (Object.keys(checksums).length > 0) {
    await addEntry(CHECKSUMS_PATH, encoder.encode(JSON.stringify({ algorithm: CHECKSUM_ALGORITHM, checksums })));
    for (const entry of manifest.sequences) {
      entry.checksum = checksums[entry.id];
    }
  }

  zip.end();
  await drain();
  params.onProgress?.({ bytesWritten, chunksWritten, totalChunks });
  return manifest;
}

// ---------------------------------------------------------------- 読み込み

export interface GprojReadHandlers {
  onManifest?: (manifest: GprojManifest) => Promise<void> | void;
  onFeatures?: (sequenceId: string, features: GenomicFeatureV1[]) => Promise<void> | void;
  onChunk?: (sequenceId: string, chunkIndex: number, codes: Uint8Array) => Promise<void> | void;
  onProgress?: (progress: { bytesRead: number; expandedBytes: number }) => void;
}

export interface GprojReadResult {
  manifest: GprojManifest;
  /** 実際に読んだチャンク数（配列 ID ごと） */
  chunkCounts: Record<string, number>;
  /** 配列 ID ごとの塩基数（v1/v2 互換読み込みでは実測値） */
  lengths: Record<string, number>;
  checksums: Record<string, string>;
  warnings: string[];
}

interface ParsedEntryPath {
  kind: 'manifest' | 'features' | 'chunk' | 'legacy-sequence' | 'checksums' | 'unknown';
  sequenceId?: string;
  chunkIndex?: number;
}

export function parseEntryPath(path: string): ParsedEntryPath {
  if (path === MANIFEST_PATH) return { kind: 'manifest' };
  if (path === CHECKSUMS_PATH) return { kind: 'checksums' };
  if (path === V1_FEATURES_PATH) return { kind: 'features', sequenceId: '' };
  if (path === V1_SEQUENCE_PATH) return { kind: 'legacy-sequence', sequenceId: '' };

  const chunkMatch = /^sequences\/([^/]+)\/(\d{1,10})\.bin$/.exec(path);
  if (chunkMatch) {
    return { kind: 'chunk', sequenceId: chunkMatch[1], chunkIndex: Number(chunkMatch[2]) };
  }

  const legacyMatch = /^sequences\/([^/]+)\.bin$/.exec(path);
  if (legacyMatch) return { kind: 'legacy-sequence', sequenceId: legacyMatch[1] };

  const featureMatch = /^features\/([^/]+)\.json$/.exec(path);
  if (featureMatch) return { kind: 'features', sequenceId: featureMatch[1] };

  return { kind: 'unknown' };
}

export async function readGprojStream(
  source: AsyncIterable<Uint8Array>,
  handlers: GprojReadHandlers = {}
): Promise<GprojReadResult> {
  const decoder = new TextDecoder();
  const warnings: string[] = [];
  const chunkCounts: Record<string, number> = {};
  const lengths: Record<string, number> = {};
  let checksums: Record<string, string> = {};
  const hashers: Record<string, ChunkMerkleHasher> = {};
  /** ondata（同期）内から書き換えるため、フロー解析に潰されないよう保持子に入れる */
  const state: { manifest: GprojManifest | null } = { manifest: null };

  let bytesRead = 0;
  let expandedBytes = 0;
  let entryCount = 0;
  let readerError: Error | null = null;

  /** ondata（同期）から積まれた非同期処理を順に流す */
  const tasks: Array<() => Promise<void>> = [];
  const runTasks = async () => {
    while (tasks.length > 0) {
      const task = tasks.shift() as () => Promise<void>;
      await task();
    }
    if (readerError) throw readerError;
  };

  const handleEntry = async (path: string, data: Uint8Array) => {
    const parsed = parseEntryPath(path);
    switch (parsed.kind) {
      case 'manifest': {
        if (data.length > MAX_MANIFEST_BYTES) {
          throw new GprojFormatError(`manifest.json が大きすぎます: ${data.length} バイト`);
        }
        state.manifest = JSON.parse(decoder.decode(data)) as GprojManifest;
        await handlers.onManifest?.(state.manifest);
        return;
      }
      case 'checksums': {
        const parsedJson = JSON.parse(decoder.decode(data)) as { checksums?: Record<string, string> };
        checksums = parsedJson.checksums ?? {};
        return;
      }
      case 'features': {
        const sequenceId = resolveSequenceId(parsed.sequenceId, state.manifest);
        const parsedJson = JSON.parse(decoder.decode(data)) as
          | GenomicFeatureV1[]
          | { features?: GenomicFeatureV1[] };
        const features = Array.isArray(parsedJson) ? parsedJson : parsedJson.features ?? [];
        await handlers.onFeatures?.(sequenceId, features);
        return;
      }
      case 'chunk': {
        const sequenceId = parsed.sequenceId as string;
        await handlers.onChunk?.(sequenceId, parsed.chunkIndex as number, data);
        chunkCounts[sequenceId] = (chunkCounts[sequenceId] ?? 0) + 1;
        lengths[sequenceId] = (lengths[sequenceId] ?? 0) + data.length;
        if (lengths[sequenceId] > MAX_SEQUENCE_LENGTH) {
          throw new GprojFormatError(
            `配列が上限を超えています: ${lengths[sequenceId]} bp（上限 ${MAX_SEQUENCE_LENGTH}）`
          );
        }
        if (!hashers[sequenceId]) hashers[sequenceId] = new ChunkMerkleHasher();
        await hashers[sequenceId].push(data);
        return;
      }
      case 'legacy-sequence': {
        // v1/v2 は 1 配列 1 エントリ。読み込み時にチャンクへ割り直す
        const sequenceId = resolveSequenceId(parsed.sequenceId, state.manifest);
        const chunkSize = manifestChunkSize(state.manifest, sequenceId);
        if (data.length > MAX_SEQUENCE_LENGTH) {
          throw new GprojFormatError(`配列が上限を超えています: ${data.length} bp`);
        }
        if (!hashers[sequenceId]) hashers[sequenceId] = new ChunkMerkleHasher();
        let index = 0;
        for (let offset = 0; offset < data.length; offset += chunkSize) {
          const slice = data.subarray(offset, Math.min(offset + chunkSize, data.length));
          await handlers.onChunk?.(sequenceId, index, slice);
          await hashers[sequenceId].push(slice);
          index += 1;
        }
        chunkCounts[sequenceId] = index;
        lengths[sequenceId] = data.length;
        return;
      }
      default:
        warnings.push(`未知のエントリを飛ばしました: ${path}`);
    }
  };

  const unzip = new Unzip();
  unzip.register(UnzipInflate);
  unzip.onfile = (file) => {
    entryCount += 1;
    if (entryCount > MAX_GPROJ_ENTRIES) {
      readerError = new GprojFormatError(`ZIP エントリが多すぎます（上限 ${MAX_GPROJ_ENTRIES}）`);
      return;
    }
    try {
      assertSafeEntryPath(file.name);
    } catch (error) {
      readerError = error as Error;
      return;
    }

    const parts: Uint8Array[] = [];
    let size = 0;
    file.ondata = (error, data, final) => {
      if (error) {
        readerError = error;
        return;
      }
      if (data && data.length > 0) {
        parts.push(data);
        size += data.length;
        expandedBytes += data.length;
        if (expandedBytes > MAX_GPROJ_EXPANDED_BYTES) {
          readerError = new GprojFormatError(
            `展開後のサイズが上限を超えました（上限 ${MAX_GPROJ_EXPANDED_BYTES} バイト）`
          );
          return;
        }
      }
      if (final) {
        const joined = concatBytes(parts, size);
        const name = file.name;
        tasks.push(() => handleEntry(name, joined));
      }
    };
    file.start();
  };

  for await (const chunk of source) {
    bytesRead += chunk.length;
    if (bytesRead > MAX_GPROJ_ZIP_BYTES) {
      throw new GprojFormatError(`.gproj が上限を超えています（上限 ${MAX_GPROJ_ZIP_BYTES} バイト）`);
    }
    unzip.push(chunk, false);
    await runTasks();
    handlers.onProgress?.({ bytesRead, expandedBytes });
  }
  unzip.push(new Uint8Array(0), true);
  await runTasks();

  const manifest = state.manifest;
  if (!manifest) throw new GprojFormatError('manifest.json がありません');
  for (const entry of manifest.sequences) {
    if (checksums[entry.id]) entry.checksum = checksums[entry.id];
  }

  // マニフェスト／checksums.json にある値と、読み込んだチャンクから再計算した根を照合する
  for (const entry of manifest.sequences) {
    const expected = checksums[entry.id] ?? entry.checksum;
    if (!expected) continue;
    const hasher = hashers[entry.id];
    if (!hasher) {
      throw new GprojFormatError(`チェックサム対象のチャンクがありません: ${entry.id}`);
    }
    const actual = await hasher.root();
    if (actual !== expected) {
      throw new GprojFormatError(
        `チェックサム不一致: ${entry.id}（expected ${expected}, actual ${actual}）`
      );
    }
  }

  return { manifest, chunkCounts, lengths, checksums, warnings };
}

function resolveSequenceId(rawId: string | undefined, manifest: GprojManifest | null): string {
  if (rawId) return rawId;
  return manifest?.sequences[0]?.id ?? 'seq-1';
}

function manifestChunkSize(manifest: GprojManifest | null, sequenceId: string): number {
  const entry = manifest?.sequences.find((sequence) => sequence.id === sequenceId);
  return entry?.chunkSize && entry.chunkSize > 0 ? entry.chunkSize : DEFAULT_CHUNK_SIZE;
}

function concatBytes(parts: Uint8Array[], size: number): Uint8Array {
  if (parts.length === 1) return parts[0];
  const joined = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.length;
  }
  return joined;
}
